import { Outlet } from 'react-router-dom'
import type { ReactNode } from 'react'
import { clsx } from 'clsx'
import { SidebarProvider, useSidebar } from './SidebarContext'
import { AppSidebar, type SidebarSection } from './AppSidebar'
import { AppHeader } from './AppHeader'
import type { LucideIcon } from 'lucide-react'

type Brand = { name: string; icon: LucideIcon | string }

// TailAdmin AppLayout: sidebar + (header over main). Content shifts by the sidebar's current width
// on xl+; main is max-w-screen-2xl, p-4 md:p-6. Pass sections={null} for a header-only workspace.
export function AppLayout(props: {
  brand: Brand
  homeTo: string
  sections: SidebarSection[] | null
  sidebarEmptyMessage?: string
  headerLeft?: ReactNode
  headerRight?: ReactNode
  search?: boolean
  mainClassName?: string
}) {
  return (
    <SidebarProvider>
      <AppLayoutInner {...props} />
    </SidebarProvider>
  )
}

function AppLayoutInner({ brand, homeTo, sections, sidebarEmptyMessage, headerLeft, headerRight, search, mainClassName }: Parameters<typeof AppLayout>[0]) {
  const { isExpanded, isHovered } = useSidebar()
  const withSidebar = sections !== null

  return (
    <div className="min-h-screen xl:flex">
      {withSidebar && <AppSidebar sections={sections} brand={brand} homeTo={homeTo} emptyMessage={sidebarEmptyMessage} />}
      <div
        className={clsx(
          'min-w-0 flex-1 transition-[margin] duration-300 ease-in-out',
          withSidebar && (isExpanded || isHovered ? 'xl:ml-[290px]' : 'xl:ml-[90px]'),
        )}
      >
        <AppHeader withSidebar={withSidebar} brand={brand} homeTo={homeTo} left={headerLeft} right={headerRight} search={search} />
        <main className={clsx('mx-auto p-4 md:p-6', mainClassName ?? 'max-w-[1536px]')}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
